/**
 * Video ThoughtPad notes — the watch-page twin of useReadingNotes.
 *
 * Backed by /api/videos/{id}/notes, which stores the note on the video's book +
 * chapter (so it's SHARED with Dhyana) and stamps video_id + timestamp into
 * note_metadata. Returns the same ReadingNote shape, so the Dhyana note-card UI
 * renders over it unchanged.
 *
 * `notLinked` flips true when the backend returns 409 video_not_linked (the
 * lecture has no book) — the panel shows the "ask your tutor to link a book"
 * state instead of an error.
 */
import { useState, useCallback } from 'react';
import { api } from '../config/api';
import { ReadingNote, ReadingNoteCreate } from '../types/reading-notes';

export interface UseVideoNotesReturn {
  notes: ReadingNote[];
  loading: boolean;
  error: string | null;
  notLinked: boolean;
  fetchNotes: () => Promise<void>;
  createNote: (note: Omit<ReadingNoteCreate, 'book_id' | 'page_number'>) => Promise<ReadingNote | null>;
  deleteNote: (noteId: string) => Promise<void>;
  updateNoteIndexingStatus: (noteId: string, status: Partial<ReadingNote>) => void;
}

export function useVideoNotes(videoId: string): UseVideoNotesReturn {
  const [notes, setNotes] = useState<ReadingNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notLinked, setNotLinked] = useState(false);

  const fetchNotes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/api/videos/${videoId}/notes`);
      setNotes(res.data.notes || []);
      setNotLinked(false);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === 'video_not_linked') {
        setNotLinked(true);
        setNotes([]);
      } else {
        setError(typeof detail === 'string' ? detail : 'Failed to load notes');
      }
    } finally {
      setLoading(false);
    }
  }, [videoId]);

  // book_id + page_number are resolved server-side from the video, so callers
  // never supply them — they pass note_type / content / metadata only.
  const createNote = useCallback(async (
    note: Omit<ReadingNoteCreate, 'book_id' | 'page_number'>,
  ): Promise<ReadingNote | null> => {
    try {
      setError(null);
      const res = await api.post(`/api/videos/${videoId}/notes`, {
        ...note,
        book_id: '00000000-0000-0000-0000-000000000000', // placeholder; server overrides
        page_number: 0,                                   // placeholder; server overrides
      });
      setNotes(prev => [...prev, res.data]);
      return res.data;
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === 'video_not_linked') {
        setNotLinked(true);
      } else {
        setError(typeof detail === 'string' ? detail : 'Failed to save note');
      }
      throw err;
    }
  }, [videoId]);

  const deleteNote = useCallback(async (noteId: string) => {
    try {
      await api.delete(`/api/videos/${videoId}/notes/${noteId}`);
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete note');
      throw err;
    }
  }, [videoId]);

  const updateNoteIndexingStatus = useCallback((noteId: string, status: Partial<ReadingNote>) => {
    setNotes(prev => prev.map(n => n.id === noteId ? { ...n, ...status } : n));
  }, []);

  return { notes, loading, error, notLinked, fetchNotes, createNote, deleteNote, updateNoteIndexingStatus };
}
